import { cloneDeep } from 'lodash-es';
import { ToolUserDocumentItem } from '@/models/toolUserDocument';
import { createToolUserDocumentsPageHeaders } from '@/pages/admin/toolUserDocuments/ToolUserDocumentsPageHeaders';
import { isRequestCanceled } from '@/services/baseService';
import { toolUserDocumentService } from '@/services/toolUserDocumentService';
import { createLoadingGenerationTracker } from '@/stores/composables/createLoadingGenerationTracker';
import { createStorePersistConfig } from '@/stores/composables/createStorePersistConfig';
import { useFilterPanelState } from '@/stores/composables/useFilterPanelState';
import { useFilterPersistence } from '@/stores/composables/useFilterPersistence';
import { IListParams, IPageOptions } from '@/types';
import type { IToolUserDocumentFilters } from '@/types/toolUserDocument';
import type { DataTableHeader } from '@/types/vuetify';

const defaultSortBy = [{ key: 'createdAt', order: 'desc' as const }];

function createToolUserDocumentStoreSetup(storeId: string) {
    // --- state ---
    const items = shallowRef<ToolUserDocumentItem[]>([]);
    const total = ref<number>(0);
    const loading = ref(false);
    const loadGeneration = createLoadingGenerationTracker();
    const deleteLoading = ref(false);

    // --- filters ---
    const filters = reactive<IToolUserDocumentFilters>({
        userDocumentSearch: {
            value: null,
            title: 'toolUserDocuments.filters.search',
            allowSaveState: false,
        },
        userId: {
            value: null,
            title: 'toolUsers.filters.userId',
            allowSaveState: false,
        },
        userDocumentTypeId: {
            value: null,
            title: 'toolUserDocuments.filters.documentType',
            allowSaveState: true,
        },
    });

    // --- filter panel ---
    const { hiddenFilters, visibleFiltersMobile, filterOrder, availableFilters, useButtonForFiltersApply, saveFiltersState, visibleFilters } =
        useFilterPanelState(filters);

    // --- options ---
    const options = ref<IPageOptions>({
        page: 1,
        itemsPerPage: 100,
        rowsPerPageItems: [100, 200, 500],
        sortBy: [...defaultSortBy],
    });

    // --- reset ---
    function resetFilters() {
        filters.userDocumentSearch.value = null;
        filters.userId.value = null;
        filters.userDocumentTypeId.value = null;
    }

    // --- table ---
    const headers = computed<DataTableHeader[]>(() => createToolUserDocumentsPageHeaders());
    const availableColumns = computed(() => headers.value.filter((header) => header.title));
    const hiddenColumns = ref<string[]>([]);
    const tableHeaders = computed<DataTableHeader[]>(() =>
        headers.value.filter((header) => header.key && (!hiddenColumns.value.includes(header.key) || !header.title))
    );

    // --- persistence ---
    const { reloadFiltersFromStorage } = useFilterPersistence({
        storeId,
        primaryStoreId: 'toolUserDocuments',
        filters,
        saveFiltersState,
        resetFilters,
        transform: {
            load: (data: Record<string, unknown>) => {
                if (!data || typeof data !== 'object') return data;
                const next = { ...data };
                if ('typeId' in next && !('userDocumentTypeId' in next)) {
                    (next as Record<string, unknown>).userDocumentTypeId = next.typeId;
                    delete next.typeId;
                }
                if ('userSearch' in next && !('userDocumentSearch' in next)) {
                    (next as Record<string, unknown>).userDocumentSearch = next.userSearch;
                    delete next.userSearch;
                }
                return next;
            },
        },
    });

    // --- actions ---
    async function loadData() {
        const loadId = loadGeneration.next();
        loading.value = true;
        try {
            const params: IListParams<IToolUserDocumentFilters> = {
                filters: cloneDeep(filters),
                options: {
                    sortBy: options.value.sortBy,
                    page: options.value.page,
                    itemsPerPage: options.value.itemsPerPage,
                },
                requiredData: [],
                requiredDictionaries: ['AuditTable'],
                requestKey: `tool-user-documents:${storeId}`,
            };

            const response = await toolUserDocumentService.list(params);
            items.value = response.data.rows.map((row) => new ToolUserDocumentItem(row));
            total.value = response.data.total;
        } catch (e) {
            if (isRequestCanceled(e)) return;
            throw e;
        } finally {
            if (loadGeneration.isLatest(loadId)) {
                loading.value = false;
            }
        }
    }

    function updateOptions(value: Partial<IPageOptions>) {
        if (value.page !== undefined && value.page !== options.value.page) {
            options.value.page = value.page;
            loadData();
        }
        if (value.itemsPerPage !== undefined && value.itemsPerPage !== options.value.itemsPerPage) {
            options.value.itemsPerPage = value.itemsPerPage;
            options.value.page = 1;
            loadData();
        }
        if (value.sortBy !== undefined) {
            options.value.sortBy = value.sortBy;
            loadData();
        }
    }

    async function patchValid(item: ToolUserDocumentItem, value: 0 | 1) {
        const oldValue = item.isValid;
        item.isValid = value;
        triggerRef(items);

        try {
            const response = await toolUserDocumentService.update({ rows: [{ id: item.id, is_valid: value }] });
            if (response.data.row) {
                const idx = items.value.findIndex((i) => i.id === item.id);
                if (idx !== -1) {
                    items.value[idx] = new ToolUserDocumentItem(response.data.row);
                    triggerRef(items);
                }
                return;
            }
            if (response.data.success === false) {
                item.isValid = oldValue;
                triggerRef(items);
            }
        } catch {
            item.isValid = oldValue;
            triggerRef(items);
        }
    }

    async function deleteDocument(item: ToolUserDocumentItem) {
        deleteLoading.value = true;
        try {
            const response = await toolUserDocumentService.delete({ rows: [{ id: item.id }] });
            if (response.data.success !== false) {
                await loadData();
            }
        } catch {
            /* toolUserDocumentService wraps errors */
        } finally {
            deleteLoading.value = false;
        }
    }

    function clearData() {
        items.value = [];
        total.value = 0;
        options.value.page = 1;
        loading.value = false;
        deleteLoading.value = false;
    }

    // --- return ---
    return {
        storeId,
        items,
        total,
        filters,
        hiddenFilters,
        visibleFiltersMobile,
        filterOrder,
        availableFilters,
        useButtonForFiltersApply,
        saveFiltersState,
        visibleFilters,
        resetFilters,
        reloadFiltersFromStorage,
        headers,
        tableHeaders,
        availableColumns,
        hiddenColumns,
        loading,
        deleteLoading,
        options,
        loadData,
        updateOptions,
        patchValid,
        deleteDocument,
        clearData,
    };
}

export function useToolUserDocumentStore(storeId: string = 'toolUserDocuments') {
    const useStore = defineStore(storeId, () => createToolUserDocumentStoreSetup(storeId), {
        persist: createStorePersistConfig(storeId, 'toolUserDocuments', defaultSortBy),
    });
    return useStore();
}
